package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Checklist
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material3.Checkbox
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.isCtrlPressed
import androidx.compose.ui.input.pointer.isMetaPressed
import androidx.compose.ui.input.pointer.isShiftPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.team.BulkChange
import com.evoapps.evoshift.domain.team.BulkField
import com.evoapps.evoshift.domain.team.BulkPlan
import com.evoapps.evoshift.domain.team.BulkSkip
import com.evoapps.evoshift.domain.team.PayRaise
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.viewmodels.BulkEditForm
import com.evoapps.evoshift.viewmodels.BulkEvent
import com.evoapps.evoshift.viewmodels.BulkScope
import com.evoapps.evoshift.viewmodels.BulkUiState
import com.evoapps.evoshift.viewmodels.MemberKey
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoCheckboxRow
import com.evoapps.evoshift.widgets.EvoDateField
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoSegmented
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/*
 * SH-269: the Team screen's selection and "Edit selected" (BulkEmployeeEditViewModel): the bar
 * over the list, a selectable person, the group-wide list, the sheet with only the fields to
 * change, and the summary before saving.
 */

/** Test tags of the selection and the sheet. */
object BulkTags {
    const val SELECT = "bulk_select"
    const val SELECT_ALL = "bulk_select_all"
    const val EDIT = "bulk_edit"
    const val SCOPE_GROUP = "bulk_scope_group"
    const val SCOPE_WORKPLACE = "bulk_scope_workplace"
    const val SUMMARY = "bulk_summary"
    const val SAVE = "bulk_save"
    fun member(key: MemberKey) = "bulk_member_${key.workplaceId}_${key.userId}"
    fun field(field: BulkField) = "bulk_field_${field.name.lowercase()}"
}

/** The selection bar over the team list: Select, or the count with Select all, Clear, the group and Edit selected. */
@Composable
fun BulkSelectionBar(state: BulkUiState, visible: List<MemberKey>, onEvent: (BulkEvent) -> Unit, modifier: Modifier = Modifier) {
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        if (!state.selecting) {
            TextButton(onClick = { onEvent(BulkEvent.StartSelecting) }, modifier = Modifier.align(Alignment.End).testTag(BulkTags.SELECT)) {
                Text(stringResource(Res.string.bulk_select))
            }
            return@Column
        }
        Text(stringResource(Res.string.bulk_select_hint), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f))
        state.groupName?.let { name ->
            val shown = if (state.groupUsesDefaultName) stringResource(Res.string.workplace_groups_default_name) else name
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                FilterChip(
                    selected = state.scope == BulkScope.WORKPLACE,
                    onClick = { onEvent(BulkEvent.SetScope(BulkScope.WORKPLACE)) },
                    label = { Text(stringResource(Res.string.bulk_scope_workplace)) },
                    modifier = Modifier.testTag(BulkTags.SCOPE_WORKPLACE)
                )
                FilterChip(
                    selected = state.scope == BulkScope.GROUP,
                    onClick = { onEvent(BulkEvent.SetScope(BulkScope.GROUP)) },
                    label = { Text(stringResource(Res.string.bulk_scope_group, shown)) },
                    modifier = Modifier.testTag(BulkTags.SCOPE_GROUP)
                )
            }
        }
        Text(
            stringResource(Res.string.bulk_selected_count, state.count, state.workplaceCount),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = DeepNavy
        )
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
            TextButton(onClick = { onEvent(BulkEvent.SelectAll(visible)) }, modifier = Modifier.testTag(BulkTags.SELECT_ALL)) {
                Text(stringResource(Res.string.bulk_select_all))
            }
            TextButton(onClick = { onEvent(BulkEvent.ClearSelection) }) { Text(stringResource(Res.string.bulk_clear)) }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            OutlinedButton(onClick = { onEvent(BulkEvent.StopSelecting) }, modifier = Modifier.weight(1f)) {
                Text(stringResource(Res.string.bulk_select_stop))
            }
            OutlinedButton(
                onClick = { onEvent(BulkEvent.OpenEdit) },
                enabled = state.count > 0,
                modifier = Modifier.weight(1f).testTag(BulkTags.EDIT)
            ) { Text(stringResource(Res.string.bulk_edit_selected)) }
        }
    }
}

/** Shift and Ctrl / Cmd held at the last press (desktop and web). */
private class PressModifiers {
    var range = false
    var toggle = false
}

/**
 * A person in the list who can be selected: in the selection mode a tap toggles them (Shift-click
 * selects up to them) and a checkbox shows it; otherwise a tap does [onOpen], and Ctrl/Cmd-click
 * or Shift-click starts the selection with them (desktop and web).
 */
@Composable
fun SelectableMember(
    state: BulkUiState?,
    key: MemberKey,
    visible: List<MemberKey>,
    onEvent: (BulkEvent) -> Unit,
    onOpen: (() -> Unit)?,
    content: @Composable (onClick: (() -> Unit)?, selected: Boolean) -> Unit
) {
    if (state == null) {
        content(onOpen, false)
        return
    }
    val pressed = remember { PressModifiers() }
    val selected = key in state.selected
    val click: () -> Unit = {
        when {
            state.selecting -> onEvent(BulkEvent.Toggle(key, visible, range = pressed.range))
            pressed.range || pressed.toggle -> onEvent(BulkEvent.Toggle(key, visible))
            else -> onOpen?.invoke()
        }
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().testTag(BulkTags.member(key)).pointerInput(Unit) {
            awaitPointerEventScope {
                while (true) {
                    val event = awaitPointerEvent(PointerEventPass.Initial)
                    if (event.type == PointerEventType.Press) {
                        pressed.range = event.keyboardModifiers.isShiftPressed
                        pressed.toggle = event.keyboardModifiers.isCtrlPressed || event.keyboardModifiers.isMetaPressed
                    }
                }
            }
        }
    ) {
        if (state.selecting) {
            Checkbox(checked = selected, onCheckedChange = { onEvent(BulkEvent.Toggle(key, visible)) })
        }
        Box(Modifier.weight(1f)) { content(click, selected) }
    }
}

/** The people of every workplace of the group (the owner's group-wide selection). */
@Composable
fun BulkGroupList(state: BulkUiState, onEvent: (BulkEvent) -> Unit, modifier: Modifier = Modifier) {
    val visible = remember(state.groupMembers) { state.groupMembers.map { it.key } }
    LazyColumn(modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        items(state.groupMembers, key = { "${it.key.workplaceId}:${it.key.userId}" }) { member ->
            SelectableMember(state, member.key, visible, onEvent, onOpen = null) { onClick, selected ->
                RegularCard(onClick = onClick, modifier = Modifier.fillMaxWidth()) {
                    Column(Modifier.padding(16.dp)) {
                        Text(member.user.displayName, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = if (selected) DarkGold else DeepNavy)
                        val role = member.user.engagements[member.key.workplaceId]?.role ?: stringResource(Res.string.skeleton_no_role)
                        Text("${member.workplaceName} · $role", style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.7f))
                    }
                }
            }
        }
    }
}

/** The sheet: each field off ("leave unchanged") or on with its value. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun BulkEditDialog(state: BulkUiState, onEvent: (BulkEvent) -> Unit) {
    val form = state.form
    val update = { change: (BulkEditForm) -> BulkEditForm -> onEvent(BulkEvent.UpdateForm(change(form))) }
    val problems = form.problems
    val dateError = stringResource(Res.string.bulk_problem_date)
    EvoDialog(
        title = stringResource(Res.string.bulk_edit_title),
        subtitle = stringResource(Res.string.bulk_selected_count, state.count, state.workplaceCount),
        icon = Icons.Default.Groups,
        onDismiss = { onEvent(BulkEvent.CloseEdit) },
        width = DialogWidth.Wide,
        dismissOnClickOutside = false,
        confirm = DialogAction(
            stringResource(Res.string.bulk_show_summary),
            { onEvent(BulkEvent.ShowSummary) },
            enabled = problems.isEmpty() && !state.isWorking,
            loading = state.isWorking
        )
    ) {
        Text(stringResource(Res.string.bulk_edit_hint), style = MaterialTheme.typography.bodySmall)
        if (!state.canEditPay) Text(stringResource(Res.string.bulk_finances_only), style = MaterialTheme.typography.bodySmall)

        if (state.canEditPay) {
            EvoCheckboxRow(form.pay, { on -> update { it.copy(pay = on) } }, stringResource(Res.string.bulk_field_pay), Modifier.testTag(BulkTags.field(BulkField.PAY)))
            if (form.pay) {
                EvoSegmented(
                    options = listOf(PayRaise.Kind.PERCENT, PayRaise.Kind.AMOUNT),
                    selected = form.payKind,
                    onSelect = { kind -> update { it.copy(payKind = kind) } },
                    optionLabel = { stringResource(if (it == PayRaise.Kind.PERCENT) Res.string.bulk_pay_percent else Res.string.bulk_pay_amount) }
                )
                EvoTextField(
                    value = form.payValue,
                    onValueChange = { v -> update { it.copy(payValue = v) } },
                    label = stringResource(Res.string.bulk_pay_value),
                    keyboardType = KeyboardType.Decimal,
                    suffix = if (form.payKind == PayRaise.Kind.PERCENT) "%" else null,
                    supportingText = stringResource(Res.string.bulk_pay_value_hint),
                    error = stringResource(Res.string.bulk_problem_pay).takeIf { form.payValue.isNotBlank() && BulkEditForm.Problem.PAY_VALUE in problems }
                )
                EvoDateField(
                    value = form.payFrom,
                    onValueChange = { v -> update { it.copy(payFrom = v) } },
                    label = stringResource(Res.string.bulk_effective_from),
                    error = dateError.takeIf { form.payFrom.isNotBlank() && BulkEditForm.Problem.PAY_DATE in problems }
                )
                Text(stringResource(Res.string.bulk_effective_hint), style = MaterialTheme.typography.bodySmall)
            }
        }

        EvoCheckboxRow(form.role, { on -> update { it.copy(role = on) } }, stringResource(Res.string.bulk_field_role), Modifier.testTag(BulkTags.field(BulkField.ROLE)))
        if (form.role) {
            if (state.roles.isNotEmpty()) {
                EvoDropdown(
                    label = stringResource(Res.string.bulk_field_role),
                    options = state.roles,
                    selected = form.roleValue.takeIf { it in state.roles },
                    onSelected = { role -> update { it.copy(roleValue = role) } },
                    optionLabel = { it }
                )
            } else {
                EvoTextField(
                    value = form.roleValue,
                    onValueChange = { v -> update { it.copy(roleValue = v) } },
                    label = stringResource(Res.string.bulk_field_role),
                    error = stringResource(Res.string.bulk_problem_role).takeIf { BulkEditForm.Problem.ROLE in problems }
                )
            }
        }

        if (state.skills.isNotEmpty()) {
            EvoCheckboxRow(form.skills, { on -> update { it.copy(skills = on) } }, stringResource(Res.string.bulk_field_skills), Modifier.testTag(BulkTags.field(BulkField.SKILLS)))
            if (form.skills) {
                SkillChips(Res.string.bulk_skills_add, state.skills, form.skillsAdd) { skill, on ->
                    update { it.copy(skillsAdd = if (on) it.skillsAdd + skill else it.skillsAdd - skill, skillsRemove = it.skillsRemove - skill) }
                }
                SkillChips(Res.string.bulk_skills_remove, state.skills, form.skillsRemove) { skill, on ->
                    update { it.copy(skillsRemove = if (on) it.skillsRemove + skill else it.skillsRemove - skill, skillsAdd = it.skillsAdd - skill) }
                }
            }
        }

        if (state.canEditPay) {
            EvoCheckboxRow(form.contract, { on -> update { it.copy(contract = on) } }, stringResource(Res.string.bulk_field_contract), Modifier.testTag(BulkTags.field(BulkField.CONTRACT)))
            if (form.contract) {
                val types = listOf<SystemContractType?>(null) + SelectableContracts
                EvoDropdown(
                    label = stringResource(Res.string.employee_contract_type),
                    options = types,
                    selected = form.contractType?.let { SystemContractType.fromId(it) },
                    onSelected = { type -> update { it.copy(contractType = type?.name) } },
                    optionLabel = { it?.let { type -> contractLabel(type) } ?: stringResource(Res.string.bulk_contract_unchanged) }
                )
                EvoTextField(
                    value = form.declaredHours,
                    onValueChange = { v -> update { it.copy(declaredHours = v.filter { c -> c.isDigit() }.take(3)) } },
                    label = stringResource(Res.string.bulk_declared_hours),
                    keyboardType = KeyboardType.Number,
                    error = stringResource(Res.string.bulk_problem_contract).takeIf { BulkEditForm.Problem.CONTRACT in problems }
                )
                EvoDateField(
                    value = form.contractFrom,
                    onValueChange = { v -> update { it.copy(contractFrom = v) } },
                    label = stringResource(Res.string.bulk_effective_from),
                    error = dateError.takeIf { form.contractFrom.isNotBlank() && BulkEditForm.Problem.CONTRACT_DATE in problems }
                )
            }
            EvoCheckboxRow(form.end, { on -> update { it.copy(end = on) } }, stringResource(Res.string.bulk_field_end), Modifier.testTag(BulkTags.field(BulkField.END)))
            if (form.end) {
                EvoDateField(
                    value = form.endOn,
                    onValueChange = { v -> update { it.copy(endOn = v) } },
                    label = stringResource(Res.string.bulk_end_on),
                    error = dateError.takeIf { form.endOn.isNotBlank() && BulkEditForm.Problem.END_DATE in problems }
                )
            }
        }
        if (BulkEditForm.Problem.NOTHING_CHOSEN in problems) {
            Text(stringResource(Res.string.bulk_problem_nothing), style = MaterialTheme.typography.bodySmall)
        }
        if (BulkEditForm.Problem.SKILLS in problems) {
            Text(stringResource(Res.string.bulk_problem_skills), style = MaterialTheme.typography.bodySmall)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SkillChips(title: StringResource, skills: List<String>, chosen: Set<String>, onToggle: (String, Boolean) -> Unit) {
    Text(stringResource(title), style = MaterialTheme.typography.labelLarge)
    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        skills.forEach { skill ->
            FilterChip(selected = skill in chosen, onClick = { onToggle(skill, skill !in chosen) }, label = { Text(skill) })
        }
    }
}

/** The summary: "People: N, workplaces: M", each person's changes from what to what, and what is skipped (why). */
@Composable
fun BulkSummaryDialog(plan: BulkPlan, isSaving: Boolean, currency: (String) -> String, onEvent: (BulkEvent) -> Unit) {
    EvoDialog(
        title = stringResource(Res.string.bulk_summary_title),
        subtitle = stringResource(Res.string.bulk_summary_count, plan.people, plan.workplaces),
        icon = Icons.Default.Checklist,
        onDismiss = { onEvent(BulkEvent.BackToForm) },
        width = DialogWidth.Wide,
        dismissible = !isSaving,
        dismiss = DialogAction(stringResource(Res.string.back), { onEvent(BulkEvent.BackToForm) }),
        confirm = DialogAction(stringResource(Res.string.bulk_save), { onEvent(BulkEvent.Save) }, enabled = !plan.isEmpty && !isSaving, loading = isSaving)
    ) {
        Column(Modifier.testTag(BulkTags.SUMMARY), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            if (plan.isEmpty) Text(stringResource(Res.string.bulk_summary_nothing))
            plan.lines.forEach { line ->
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text("${line.target.name} · ${line.target.workplaceName}", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    line.changes.forEach { change -> Text(changeText(change, currency(line.target.workplaceId)), style = MaterialTheme.typography.bodySmall) }
                    line.skipped.forEach { (field, why) ->
                        Text(stringResource(Res.string.bulk_skip_line, fieldName(field), skipText(why)), style = MaterialTheme.typography.bodySmall)
                    }
                }
            }
        }
    }
}

@Composable
private fun fieldName(field: BulkField): String = stringResource(
    when (field) {
        BulkField.PAY -> Res.string.bulk_field_pay
        BulkField.CONTRACT -> Res.string.bulk_field_contract
        BulkField.END -> Res.string.bulk_field_end
        BulkField.ROLE -> Res.string.bulk_field_role
        BulkField.SKILLS -> Res.string.bulk_field_skills
    }
)

@Composable
private fun skipText(why: BulkSkip): String = stringResource(
    when (why) {
        BulkSkip.NOT_ALLOWED -> Res.string.bulk_skip_not_allowed
        BulkSkip.NO_PAY_TERMS -> Res.string.bulk_skip_no_pay_terms
        BulkSkip.MONTH_CLOSED -> Res.string.bulk_skip_month_closed
        BulkSkip.BEFORE_START -> Res.string.bulk_skip_before_start
        BulkSkip.ALREADY_ENDED -> Res.string.bulk_skip_already_ended
        BulkSkip.NO_CHANGE -> Res.string.bulk_skip_no_change
    }
)

@Composable
private fun payText(pay: CompensationModel, currency: String): String = when (pay) {
    is CompensationModel.Hourly -> stringResource(Res.string.bulk_pay_hourly, formatMoney(pay.rate, currency))
    is CompensationModel.MonthlyFixed -> stringResource(Res.string.bulk_pay_monthly, formatMoney(pay.baseSalary, currency))
}

@Composable
private fun changeText(change: BulkChange, currency: String): String {
    val none = stringResource(Res.string.bulk_none)
    val name = fieldName(change.field)
    return when (change) {
        is BulkChange.Pay -> stringResource(
            Res.string.bulk_change_from_day,
            stringResource(Res.string.bulk_change_line, name, payText(change.before, currency), payText(change.after, currency)),
            change.from.toString()
        )
        is BulkChange.Contract -> {
            val before = contractLabel(SystemContractType.fromId(change.beforeType)) +
                (change.beforeHours?.let { ", " + stringResource(Res.string.bulk_hours_value, it) } ?: "")
            val after = contractLabel(SystemContractType.fromId(change.afterType)) +
                (change.afterHours?.let { ", " + stringResource(Res.string.bulk_hours_value, it) } ?: "")
            stringResource(Res.string.bulk_change_from_day, stringResource(Res.string.bulk_change_line, name, before, after), change.from.toString())
        }
        is BulkChange.End -> stringResource(Res.string.bulk_change_line, name, change.before ?: none, change.after.toString())
        is BulkChange.Role -> stringResource(Res.string.bulk_change_line, name, change.before ?: none, change.after)
        is BulkChange.Skills -> stringResource(
            Res.string.bulk_change_line, name,
            change.before.joinToString(", ").ifEmpty { none },
            change.after.joinToString(", ").ifEmpty { none }
        )
    }
}
